import { Player, Recorder } from "@togglhire/video";
import { Dispatch, useState } from "react";
import { useInterval, useMedia } from "react-use";

import { Action } from "@/components/test/reducer";
import {
  VIDEO_ASPECT_RATIO,
  VIDEO_RESOLUTION,
} from "@/components/test/utils/video-resolution";
import { PublicQuestion } from "@/queries/types";
import { Video } from "@/types/test";

interface VideoAnswerProps {
  isRecording: boolean;
  question: PublicQuestion;
  answer?: Video;
  endsAt: Date | null;
  dispatch: Dispatch<Action>;
  onStartRecording: () => void;
  onFinishRecording: (blob: Blob, objectUrl: string) => void;
}

const DEFAULT_TIME_LIMIT = 2 * 60;

export function VideoAnswer(props: VideoAnswerProps) {
  const isMobile = useMedia("(max-width: 767px)");
  const videoUrl = props.answer?.objectUrl;

  if (!videoUrl) {
    return <Record {...props} />;
  } else {
    return (
      <Player
        key={videoUrl}
        src={videoUrl}
        aspectRatio={
          isMobile
            ? VIDEO_ASPECT_RATIO["portrait"]
            : VIDEO_ASPECT_RATIO["landscape"]
        }
      />
    );
  }
}

function Record(props: VideoAnswerProps) {
  const { question, endsAt } = props;
  const questionId = question.test_question_id;
  const [timeLimit, setTimeLimit] = useState(DEFAULT_TIME_LIMIT);
  const isMobile = useMedia("(max-width: 767px)");
  /**
   * This hook ensures that component gets the latest Date.now(),
   * instead of using Date.now() when component rendered for the first time.
   * It also speeds up the timer by 2sec, to ensure that the video recording
   * is ready before final test submission
   */
  useInterval(
    () => {
      setTimeLimit(
        Math.min(
          endsAt
            ? (endsAt.getTime() - Date.now() - 2000) / 1000
            : DEFAULT_TIME_LIMIT,
          DEFAULT_TIME_LIMIT,
        ),
      );
    },
    props.isRecording ? null : 200,
  );

  return (
    <Recorder
      timeLimit={timeLimit}
      resolution={VIDEO_RESOLUTION[isMobile ? "portrait" : "landscape"]}
      onStart={props.onStartRecording}
      onFinish={props.onFinishRecording}
    />
  );
}
